import { formatDate } from "@/lib/utils";
import React, { useState, useMemo, useCallback } from "react";
import {
  PlusCircle,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  Play,
  Pause,
  Zap,
  Loader2,
} from "lucide-react";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import {
  FilterCard,
  FilterSearch,
  FilterActions,
} from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table as ShadcnTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import apiClient from "../../../api/apiClient";
import { API_ENDPOINTS } from "../../../api/header";
import { useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { all_routes } from "../../../Router/all_routes";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PageContainer } from "@/components/layout/PageContainer";

const statusVariants = {
  active: "success",
  paused: "warning",
  completed: "info",
  cancelled: "destructive",
};

const frequencyLabels = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly",
};

const RecurringInvoicesList = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [frequencyFilter, setFrequencyFilter] = useState("all");

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewRecurringId, setViewRecurringId] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    action: "",
    id: null,
    title: "",
    description: "",
  });

  const handleActionClick = (action, id, title, description) => {
    setConfirmDialog({
      isOpen: true,
      action,
      id,
      title,
      description,
    });
  };

  const { data: viewRecurringData, isLoading: isViewLoading } = useQuery({
    queryKey: ["recurring-invoice", viewRecurringId],
    queryFn: async () => {
      const res = await apiClient.get(
        API_ENDPOINTS.RECURRING_INVOICES.RECURRING_INVOICE_DETAILS(
          viewRecurringId,
        ) + "?with[]=items&with[]=customer",
      );
      return res.data.data;
    },
    enabled: !!viewRecurringId && viewModalOpen,
  });

  const handleView = (recurring) => {
    setViewRecurringId(recurring.id);
    setViewModalOpen(true);
  };

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const currentStore = useSelector((state) => state.current_store);
  const currency = currentStore?.default_currency;
  const currencySymbol = currency?.symbol || "";

  const permissions = useSelector((state) => state.userpermission_data);
  const hasPermission = useCallback(
    (perm) => permissions?.includes(perm),
    [permissions],
  );

  const { data: recurringData, isLoading } = useQuery({
    queryKey: [
      "recurring-invoices",
      pagination.pageIndex + 1,
      pagination.pageSize,
      searchText,
      statusFilter,
      frequencyFilter,
      currentStore?.id,
    ],
    queryFn: async () => {
      const params = {
        page: pagination.pageIndex + 1,
        perPage: pagination.pageSize,
        with: ["customer", "store", "items"],
      };
      if (currentStore?.id) params.store_id = currentStore.id;
      if (searchText) params.search = searchText;
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (frequencyFilter && frequencyFilter !== "all")
        params.frequency = frequencyFilter;

      const res = await apiClient.get(
        API_ENDPOINTS.RECURRING_INVOICES.RECURRING_INVOICES,
        {
          params,
        },
      );
      return res.data;
    },
  });

  const recurringInvoices = recurringData?.data || [];
  const meta = recurringData?.meta || {};
  const pageCount = meta.last_page || 1;

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await apiClient.delete(
        API_ENDPOINTS.RECURRING_INVOICES.RECURRING_INVOICE_DETAILS(id),
      );
    },
    onSuccess: () => {
      toast.success("Recurring invoice cancelled successfully");
      queryClient.invalidateQueries({ queryKey: ["recurring-invoices"] });
      setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to cancel recurring invoice",
      );
      setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
    },
  });

  const actionMutation = useMutation({
    mutationFn: async ({ id, action }) => {
      let endpoint;
      if (action === "pause")
        endpoint = API_ENDPOINTS.RECURRING_INVOICES.PAUSE(id);
      else if (action === "resume")
        endpoint = API_ENDPOINTS.RECURRING_INVOICES.RESUME(id);
      else if (action === "generate")
        endpoint = API_ENDPOINTS.RECURRING_INVOICES.GENERATE(id);

      const res = await apiClient.post(endpoint, {});
      return { res, action };
    },
    onSuccess: (data) => {
      if (data.action === "generate") {
        toast.success("Invoice generated successfully");
      } else {
        toast.success(`Recurring invoice ${data.action}d successfully`);
      }
      queryClient.invalidateQueries({ queryKey: ["recurring-invoices"] });
      setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to perform action");
      setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
    },
  });

  const columns = useMemo(
    () => [
      {
        accessorKey: "recurring_number",
        header: "Schedule #",
        cell: ({ getValue }) => (
          <span className="font-medium">{getValue()}</span>
        ),
      },
      {
        accessorFn: (row) => row.customer?.name || row.customer?.full_name,
        header: "Customer",
        cell: ({ getValue }) =>
          getValue() || <span className="text-muted-foreground">-</span>,
      },
      {
        accessorKey: "frequency",
        header: "Frequency",
        cell: ({ row }) => {
          const freq = row.original.frequency;
          const interval = row.original.interval;
          if (interval > 1) {
            return `Every ${interval} ${freq === "daily" ? "days" : freq.replace("ly", "s")}`;
          }
          return frequencyLabels[freq] || freq;
        },
      },
      {
        accessorKey: "next_due_date",
        header: "Next Due Date",
        cell: ({ getValue }) =>
          getValue() ? formatDate(getValue()) : "-",
      },
      {
        accessorKey: "total",
        header: "Total",
        cell: ({ getValue }) =>
          getValue() != null
            ? `${currencySymbol}${parseFloat(getValue()).toFixed(2)}`
            : "-",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => {
          const status = getValue();
          return (
            <Badge
              variant={statusVariants[status] || "default"}
              className="capitalize"
            >
              {status || "active"}
            </Badge>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const recurring = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                {hasPermission("recurring invoice read") && (
                  <DropdownMenuItem onClick={() => handleView(recurring)}>
                    <Eye className="mr-2 h-4 w-4" />
                    View Details
                  </DropdownMenuItem>
                )}
                {hasPermission("recurring invoice update") && (
                  <DropdownMenuItem
                    onClick={() =>
                      navigate(
                        all_routes.editRecurringInvoice.replace(
                          ":id",
                          recurring.id,
                        ),
                      )
                    }
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                )}
                {hasPermission("recurring invoice update") &&
                  recurring.status === "active" && (
                    <DropdownMenuItem
                      onClick={() =>
                        handleActionClick(
                          "generate",
                          recurring.id,
                          "Generate Invoice Now",
                          "Are you sure you want to manually generate an invoice for this schedule right now?",
                        )
                      }
                    >
                      <Zap className="mr-2 h-4 w-4 text-amber-500" />
                      Generate Now
                    </DropdownMenuItem>
                  )}
                {hasPermission("recurring invoice pause") &&
                  recurring.status === "active" && (
                    <DropdownMenuItem
                      onClick={() =>
                        handleActionClick(
                          "pause",
                          recurring.id,
                          "Pause Schedule",
                          "Are you sure you want to pause this recurring schedule? No invoices will be generated until it is resumed.",
                        )
                      }
                    >
                      <Pause className="mr-2 h-4 w-4" />
                      Pause Schedule
                    </DropdownMenuItem>
                  )}
                {hasPermission("recurring invoice resume") &&
                  recurring.status === "paused" && (
                    <DropdownMenuItem
                      onClick={() =>
                        handleActionClick(
                          "resume",
                          recurring.id,
                          "Resume Schedule",
                          "Are you sure you want to resume this recurring schedule? It will continue generating invoices according to its frequency.",
                        )
                      }
                    >
                      <Play className="mr-2 h-4 w-4" />
                      Resume Schedule
                    </DropdownMenuItem>
                  )}
                {hasPermission("recurring invoice delete") &&
                  recurring.status !== "cancelled" && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-red-600 focus:text-red-600"
                        onClick={() =>
                          handleActionClick(
                            "cancel",
                            recurring.id,
                            "Cancel Schedule",
                            "Are you sure you want to cancel this recurring schedule? This action cannot be undone.",
                          )
                        }
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Cancel Schedule
                      </DropdownMenuItem>
                    </>
                  )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [currencySymbol, navigate, hasPermission],
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Recurring Invoices"
          subtitle="Manage automated invoicing schedules"
        />
        <PageHeaderActions>
          {hasPermission("recurring invoice create") && (
            <Button onClick={() => navigate(all_routes.newRecurringInvoice)}>
              <PlusCircle className="mr-2 h-4 w-4" />
              New Schedule
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <FilterSearch
          placeholder="Search schedules..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <FilterActions>
          <Select
            value={frequencyFilter}
            onValueChange={(val) => {
              setFrequencyFilter(val);
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Frequency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Frequencies</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={statusFilter}
            onValueChange={(val) => {
              setStatusFilter(val);
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </FilterActions>
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={recurringInvoices}
          pageCount={pageCount}
          pagination={pagination}
          onPaginationChange={setPagination}
          isLoading={isLoading}
          onRowClick={(row) => handleView(row)}
          footerInfo={
            <div className="flex items-center gap-4">
              <span className="font-medium text-foreground">
                Total Schedules: {meta.total || 0}
              </span>
            </div>
          }
        />
      </TableCard>

      {/* View Recurring Invoice Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto px-1">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Schedule Details -{" "}
              {viewRecurringData?.recurring_number || "Loading..."}
            </DialogTitle>
            <DialogDescription>
              Details for this recurring invoice schedule including customer,
              frequency, and financial summary.
            </DialogDescription>
          </DialogHeader>

          {isViewLoading ? (
            <div className="flex items-center justify-center py-10">
              <span className="text-muted-foreground animate-pulse">
                Loading details...
              </span>
            </div>
          ) : (
            viewRecurringData && (
              <div className="space-y-6 px-5 pb-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        Schedule Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Schedule #:
                        </span>
                        <span className="font-medium">
                          {viewRecurringData.recurring_number}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Status:</span>
                        <Badge
                          variant={
                            statusVariants[viewRecurringData.status] ||
                            "default"
                          }
                          className="capitalize"
                        >
                          {viewRecurringData.status || "active"}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Frequency:
                        </span>
                        <span className="font-medium capitalize">
                          {viewRecurringData.interval > 1
                            ? `Every ${viewRecurringData.interval} ${viewRecurringData.frequency === "daily" ? "days" : viewRecurringData.frequency.replace("ly", "s")}`
                            : frequencyLabels[viewRecurringData.frequency] ||
                            viewRecurringData.frequency}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Start Date:
                        </span>
                        <span className="font-medium">
                          {formatDate(viewRecurringData.start_date,)}
                        </span>
                      </div>
                      {viewRecurringData.end_date && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            End Date:
                          </span>
                          <span className="font-medium">
                            {formatDate(viewRecurringData.end_date,)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Next Due Date:
                        </span>
                        <span className="font-medium text-blue-600">
                          {viewRecurringData.next_due_date
                            ? formatDate(viewRecurringData.next_due_date,)
                            : "-"}
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        Customer Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Customer:</span>
                        <span className="font-medium">
                          {viewRecurringData.customer?.name ||
                            viewRecurringData.customer?.full_name ||
                            "-"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Email:</span>
                        <span className="font-medium">
                          {viewRecurringData.customer?.email || "-"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Phone:</span>
                        <span className="font-medium">
                          {viewRecurringData.customer?.phone || "-"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Store:</span>
                        <span className="font-medium">
                          {viewRecurringData.store?.name || "-"}
                        </span>
                      </div>
                      <div className="flex justify-between mt-2 pt-2 border-t">
                        <span className="text-muted-foreground">
                          Auto-Send Email:
                        </span>
                        <span className="font-medium">
                          {viewRecurringData.auto_send_email ? "Yes" : "No"}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Line Items</CardTitle>
                  </CardHeader>
                  <CardContent className="px-0">
                    <ShadcnTable>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px] text-center">
                            #
                          </TableHead>
                          <TableHead>Item</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Price</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {viewRecurringData.items?.length > 0 ? (
                          viewRecurringData.items.map((item, index) => (
                            <TableRow key={item.id || index}>
                              <TableCell className="text-center align-top pt-3">
                                {index + 1}
                              </TableCell>
                              <TableCell className="align-top pt-3">
                                <div className="font-medium">
                                  {item.item_name || item.product?.name}
                                </div>
                                {item.item_sku && (
                                  <div className="text-xs text-muted-foreground">
                                    SKU: {item.item_sku}
                                  </div>
                                )}
                                {(parseFloat(item.discount_amount || 0) > 0 ||
                                  parseFloat(item.tax_amount || 0) > 0) && (
                                    <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                                      {parseFloat(item.discount_amount || 0) >
                                        0 && (
                                          <div>
                                            Disc:{" "}
                                            {item.discount_type === "percentage"
                                              ? `${parseFloat(item.discount_value).toFixed(2)}%`
                                              : `${currencySymbol}${parseFloat(item.discount_amount).toFixed(2)}`}
                                          </div>
                                        )}
                                      {parseFloat(item.tax_amount || 0) > 0 && (
                                        <div>
                                          Tax: {currencySymbol}
                                          {parseFloat(item.tax_amount).toFixed(2)}
                                        </div>
                                      )}
                                    </div>
                                  )}
                              </TableCell>
                              <TableCell className="text-right align-top pt-3">
                                {parseFloat(item.quantity).toFixed(0)}
                              </TableCell>
                              <TableCell className="text-right align-top pt-3">
                                {currencySymbol}
                                {parseFloat(item.unit_price).toFixed(2)}
                              </TableCell>
                              <TableCell className="text-right font-medium align-top pt-3">
                                {currencySymbol}
                                {parseFloat(item.line_total).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              className="h-24 text-center text-muted-foreground"
                            >
                              No line items found.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </ShadcnTable>

                    <div className="flex justify-end pt-4 px-6 mt-4 border-t">
                      <div className="w-64 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">
                            Subtotal:
                          </span>
                          <span className="font-medium">
                            {currencySymbol}
                            {parseFloat(
                              viewRecurringData.subtotal || 0,
                            ).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Tax:</span>
                          <span className="font-medium">
                            {currencySymbol}
                            {parseFloat(
                              viewRecurringData.tax_amount || 0,
                            ).toFixed(2)}
                          </span>
                        </div>
                        {parseFloat(viewRecurringData.discount_amount || 0) >
                          0 && (
                            <div className="flex justify-between text-sm text-destructive">
                              <span>Discount:</span>
                              <span className="font-medium">
                                -{currencySymbol}
                                {parseFloat(
                                  viewRecurringData.discount_amount,
                                ).toFixed(2)}
                              </span>
                            </div>
                          )}
                        <div className="flex justify-between font-bold text-lg pt-2 border-t text-green-600">
                          <span>Total:</span>
                          <span>
                            {currencySymbol}
                            {parseFloat(viewRecurringData.total || 0).toFixed(
                              2,
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {viewRecurringData.notes && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Notes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm whitespace-pre-wrap text-muted-foreground">
                        {viewRecurringData.notes}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog
        open={confirmDialog.isOpen}
        onOpenChange={(open) =>
          setConfirmDialog((prev) => ({ ...prev, isOpen: open }))
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDialog.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={actionMutation.isPending || deleteMutation.isPending}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className={
                confirmDialog.action === "cancel"
                  ? "bg-red-600 hover:bg-red-700 focus:ring-red-600"
                  : ""
              }
              disabled={actionMutation.isPending || deleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (confirmDialog.action === "cancel") {
                  deleteMutation.mutate(confirmDialog.id);
                } else {
                  actionMutation.mutate({
                    id: confirmDialog.id,
                    action: confirmDialog.action,
                  });
                }
              }}
            >
              {(actionMutation.isPending || deleteMutation.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default RecurringInvoicesList;
